import { useState } from "react";
import { CheckCircle2, Send } from "lucide-react";
import WhatsAppIcon from "./WhatsAppIcon";
import { Reveal, SectionHeading } from "./Reveal";
import { useSite } from "@/lib/site";
import { api, waLink, apiError } from "@/lib/api";

const inputCls =
  "w-full rounded-xl border border-line bg-cream px-4 py-3 text-sm text-ink transition focus:border-gold focus:bg-white focus:outline-none";

const STR = {
  ar: {
    waTitle: "الدعم عبر واتساب",
    waText: "أسرع طريقة للتواصل معنا — اضغط الزر وابدأ المحادثة مباشرة، سواء أنت عميل أو صاحب صالون.",
    phName: "الاسم الكامل *",
    phPhone: "رقم الهاتف *",
    phSalon: "اسم الصالون (اختياري)",
    phMsg: "رسالتك *",
    send: "إرسال الاستفسار",
    sending: "جارٍ الإرسال…",
    errName: "أدخل اسمك الكامل",
    errPhone: "أدخل رقم هاتف صحيحاً",
    errMsg: "اكتب رسالتك (5 أحرف على الأقل)",
    errGeneric: "حدث خطأ، حاول مرة أخرى.",
    okTitle: "تم إرسال استفسارك بنجاح",
    okText: "شكراً لتواصلك معنا، سنرد عليك في أقرب وقت.",
    okAgain: "إرسال استفسار آخر",
  },
  fr: {
    waTitle: "Support via WhatsApp",
    waText: "Le moyen le plus rapide de nous joindre — cliquez sur le bouton et commencez la discussion, que vous soyez client ou propriétaire de salon.",
    phName: "Nom complet *",
    phPhone: "Numéro de téléphone *",
    phSalon: "Nom du salon (facultatif)",
    phMsg: "Votre message *",
    send: "Envoyer la demande",
    sending: "Envoi en cours…",
    errName: "Entrez votre nom complet",
    errPhone: "Entrez un numéro de téléphone valide",
    errMsg: "Écrivez votre message (5 caractères au moins)",
    errGeneric: "Une erreur est survenue, veuillez réessayer.",
    okTitle: "Votre demande a bien été envoyée",
    okText: "Merci de nous avoir contactés, nous vous répondrons au plus vite.",
    okAgain: "Envoyer une autre demande",
  },
};

export default function Contact({ content }) {
  const { lang } = useSite();
  const S = STR[lang] || STR.ar;
  const c = content?.contact;
  const wa = waLink(c?.whatsappNumber, c?.whatsappMessage);
  const [form, setForm] = useState({ name: "", phone: "", salon_name: "", message: "", website: "" });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.name.trim().length < 2) return setError(S.errName);
    if (!/^[+\d][\d\s\-()]{7,19}$/.test(form.phone.trim())) return setError(S.errPhone);
    if (form.message.trim().length < 5) return setError(S.errMsg);
    try {
      setStatus("sending");
      await api.post("/inquiries", form);
      setStatus("success");
    } catch (err) {
      setStatus("idle");
      setError(apiError(err) || S.errGeneric);
    }
  };

  const reset = () => {
    setForm({ name: "", phone: "", salon_name: "", message: "", website: "" });
    setStatus("idle");
  };

  return (
    <section id="contact" className="bg-cream py-24 sm:py-28">
      <div className="mx-auto max-w-7xl px-5">
        <SectionHeading eyebrow={c?.eyebrow} title={c?.title} subtitle={c?.subtitle} />
        <div className="mt-14 grid gap-8 lg:grid-cols-2">
          <Reveal>
            <div
              className="grain relative flex h-full flex-col justify-center overflow-hidden rounded-[2rem] bg-forest-950 p-10 text-cream"
              data-testid="contact-whatsapp-card"
            >
              <div className="pointer-events-none absolute -left-16 -top-16 h-56 w-56 rounded-full bg-gold/15 blur-3xl" aria-hidden="true" />
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-gold/15">
                <WhatsAppIcon className="h-8 w-8 text-gold" />
              </span>
              <h3 className="mt-6 font-head text-2xl font-extrabold">{S.waTitle}</h3>
              <p className="mt-3 text-sm leading-relaxed text-cream/70">{S.waText}</p>
              <p className="num-ltr mt-5 text-lg font-semibold text-gold" data-testid="contact-whatsapp-number">
                {c?.whatsappNumber}
              </p>
              <a
                href={wa}
                target="_blank"
                rel="noreferrer"
                data-testid="contact-whatsapp-button"
                className="mt-6 inline-flex w-fit items-center gap-2 rounded-full bg-gold px-7 py-3.5 font-head font-extrabold text-forest-950 transition hover:-translate-y-0.5 hover:bg-gold-light"
              >
                <WhatsAppIcon className="h-5 w-5" />
                {c?.whatsappButton}
              </a>
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <div className="h-full rounded-[2rem] border border-line bg-white p-8 sm:p-10" data-testid="contact-form-card">
              {status === "success" ? (
                <div className="flex h-full flex-col items-center justify-center py-10 text-center" data-testid="contact-form-success">
                  <CheckCircle2 className="h-14 w-14 text-forest-700" />
                  <h3 className="mt-5 font-head text-xl font-extrabold text-forest-950">{S.okTitle}</h3>
                  <p className="mt-2 text-sm text-moss">{S.okText}</p>
                  <button
                    onClick={reset}
                    data-testid="contact-form-reset-button"
                    className="mt-6 rounded-full border border-forest-900 px-6 py-2.5 text-sm font-semibold text-forest-900 transition hover:bg-forest-900 hover:text-cream"
                  >
                    {S.okAgain}
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} noValidate>
                  <h3 className="font-head text-xl font-extrabold text-forest-950">{c?.formTitle}</h3>
                  <div className="mt-6 space-y-4">
                    <input
                      data-testid="contact-form-name"
                      className={inputCls}
                      placeholder={S.phName}
                      value={form.name}
                      onChange={set("name")}
                    />
                    <input
                      data-testid="contact-form-phone"
                      className={`${inputCls} text-left`}
                      placeholder={S.phPhone}
                      dir="ltr"
                      value={form.phone}
                      onChange={set("phone")}
                    />
                    <input
                      data-testid="contact-form-salon"
                      className={inputCls}
                      placeholder={S.phSalon}
                      value={form.salon_name}
                      onChange={set("salon_name")}
                    />
                    <textarea
                      data-testid="contact-form-message"
                      rows={4}
                      className={inputCls}
                      placeholder={S.phMsg}
                      value={form.message}
                      onChange={set("message")}
                    />
                    {/* honeypot — hidden from humans */}
                    <input
                      data-testid="contact-form-honeypot"
                      className="absolute -left-[9999px] h-0 w-0 opacity-0"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      value={form.website}
                      onChange={set("website")}
                    />
                  </div>
                  {error && (
                    <p className="mt-4 rounded-xl bg-brick/10 px-4 py-3 text-sm font-medium text-brick" data-testid="contact-form-error">
                      {error}
                    </p>
                  )}
                  <button
                    type="submit"
                    disabled={status === "sending"}
                    data-testid="contact-form-submit"
                    className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-forest-900 px-6 py-4 font-head font-extrabold text-cream transition hover:bg-forest-800 disabled:opacity-60"
                  >
                    <Send className="h-4 w-4" />
                    {status === "sending" ? S.sending : S.send}
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
